import React, { useState } from 'react';
import { Loader2, CalendarDays } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { submitBooking } from '../lib/api';
import { planOptions, timeSlots } from '../data/shop';

const BookingModal = ({ open, onOpenChange, defaultPlan }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', plan: defaultPlan || '', date: '', time: '', notes: '' });

  React.useEffect(() => {
    if (defaultPlan) setForm((f) => ({ ...f, plan: defaultPlan }));
  }, [defaultPlan]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.plan || !form.date || !form.time) {
      toast({ title: 'Missing info', description: 'Please fill name, email, plan, date and time.' });
      return;
    }
    setLoading(true);
    try {
      await submitBooking(form);
      toast({ title: 'Appointment booked!', description: `Your ${form.plan} appointment is set for ${form.date} at ${form.time}.` });
      setForm({ name: '', email: '', phone: '', plan: defaultPlan || '', date: '', time: '', notes: '' });
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CalendarDays size={20} className="text-blue-600" /> Book a Support Appointment</DialogTitle>
          <DialogDescription>Choose your plan and a convenient date & time. Our team will confirm shortly.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="b-name">Full Name</Label>
              <Input id="b-name" name="name" value={form.name} onChange={handleChange} placeholder="John Doe" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-email">Email</Label>
              <Input id="b-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="b-phone">Phone (optional)</Label>
              <Input id="b-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="(905) 909-7555" />
            </div>
            <div className="space-y-2">
              <Label>Support Plan</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {planOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="b-date">Date</Label>
              <Input id="b-date" name="date" type="date" min={today} value={form.date} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
                <SelectTrigger><SelectValue placeholder="Select a time" /></SelectTrigger>
                <SelectContent>
                  {timeSlots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="b-notes">Notes (optional)</Label>
            <Textarea id="b-notes" name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Describe your issue or request" className="resize-none" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Booking...</> : 'Confirm Appointment'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
